import type { StoreActionApi } from 'react-sweet-state';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { fetchText$ } from '@atlassian/jira-fetch';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	PACKAGE_NAME,
	withExponentialBackoffRetries,
} from '@atlassian/jira-servicedesk-getting-started-panel-common';

import {
	type BaseUrl,
	type CloudId,
	type ProjectId,
	toProjectId,
} from '@atlassian/jira-shared-types';
// @ts-expect-error - TS2305 - Module '"@atlassian/jsm-getting-started-panel"' has no exported member 'TaskIdType'.
import type { TaskIdType } from '@atlassian/jsm-getting-started-panel';

import type { State, ActionProps } from '../../types';

export type ChecklistScope = 'container' | 'user' | 'workspace';

// Data taken from https://hello.atlassian.net/wiki/spaces/VPORT/pages/808505558/Checklist+Item+Scope+Auto+API+for+Completion
// where 'project' === 'container', 'user' === 'user' and 'instance' === 'workspace'
const getScopeFromTaskId = (taskId: TaskIdType): ChecklistScope => {
	switch (taskId) {
		// String literals used instead of `TaskId.<task>` for flow to acknowledge
		// that this is an exhaustive switch statement
		case 'servicedesk-itsm-project-created':
		case 'servicedesk-portal-name-customized':
		case 'servicedesk-logo-added-to-portal':
		case 'servicedesk-email-requests-setup':
		case 'servicedesk-team-member-added':
			return 'container';
		case 'servicedesk-change-management-documentation-accessed':
		case 'servicedesk-basics-documentation-accessed':
		case 'servicedesk-queue-configurations-reviewed':
		case 'servicedesk-request-types-customised':
		case 'servicedesk-request-channels-email-portal-requests-customised':
		case 'servicedesk-chat-requests-setup':
		case 'servicedesk-team-efficiency-automate-your-work':
		case 'servicedesk-team-efficiency-setup-knowledge-base':
			return 'user';
		case 'servicedesk-services-setup':
		case 'servicedesk-change-management-pipeline-connected':
		case 'servicedesk-change-management-change-approvers-added':
		case 'servicedesk-change-management-automation-rules-on':
			return 'workspace';
		// Note that the following should only ever be completed in
		// Opsgenie but they are included here for type completeness
		case 'opsgenie-user-profile-notifications-setup':
		case 'opsgenie-incident-management-documentation-accessed':
			return 'user';
		case 'opsgenie-response-team-setup':
		case 'opsgenie-owner-team-assigned-to-service':
			return 'workspace';
		default: {
			// prettier-ignore
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(taskId as never); // type level assertion that we have an exhaustive switch
			throw new Error(`Unknown scope for task ${taskId}`); // runtime assertion
		}
	}
};

export const getUrl = ({
	baseUrl,
	cloudId,
	scope,
	projectId,
	taskId,
}: {
	baseUrl: BaseUrl;
	cloudId: CloudId;
	scope: ChecklistScope;
	projectId: ProjectId;
	taskId: TaskIdType;
}): string => {
	let scopePath = ''; // scope === 'workspace'
	if (scope === 'container') scopePath = `project/${projectId}/`;
	if (scope === 'user') scopePath = 'user/';
	return `${baseUrl}/jsm/onboarding/checklist/v1/cloudid/${cloudId}/${scopePath}list/jsm-getting-started-panel/item/${taskId}`;
};

export type ResolveTask = (arg1: TaskIdType) => Promise<void>;

export const resolveTask =
	({ baseUrl, cloudId, createAnalyticsEvent }: ActionProps) =>
	({ getState, setState }: StoreActionApi<State>): ResolveTask =>
	async (taskId: TaskIdType) => {
		const { data } = getState();

		try {
			if (!data) {
				return;
			}

			const { completedItems, properties } = data;
			const isTaskAlreadyCompleted = completedItems.includes(taskId);

			if (!isTaskAlreadyCompleted) {
				const updatedTasks = [...completedItems, taskId];

				// Set the state locally without waiting for the API call
				setState({
					data: { ...data, completedItems: updatedTasks },
					isSaving: true,
				});

				// Hit the appropriate checklist scope endpoint to add the newly completed task
				const scope = getScopeFromTaskId(taskId);
				const projectId = toProjectId(properties.user.projectId || '');
				await withExponentialBackoffRetries({
					callback: () =>
						fetchText$(getUrl({ baseUrl, cloudId, scope, projectId, taskId }), {
							method: 'POST',
							credentials: 'include',
						}).toPromise(),
					totalAttempts: 5,
					baseIntervalMs: 500,
				});

				// Resume polling for GSP state
				setState({ isSaving: false });
			}
			fireTrackAnalytics(createAnalyticsEvent({}), `${PACKAGE_NAME}.resolveTask succeeded`);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setState({ isSaving: false });
			fireErrorAnalytics({
				error,
				meta: {
					id: 'resolveTask',
					packageName: PACKAGE_NAME,
					teamName: 'itops-mithril',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			throw error;
		}
	};
